import { mat4, ReadonlyMat4, ReadonlyVec2, vec2 } from 'gl-matrix';
import { Uniform } from '~/engine/core/utils/uniforms/Uniform';
import { UniformBoolean } from '~/engine/core/utils/uniforms/UniformBoolean';
import { UniformMatrix } from '~/engine/core/utils/uniforms/UniformMatrix';
import { UniformVec2 } from '~/engine/core/utils/uniforms/UniformVec2';
import { UniformVec4 } from '~/engine/core/utils/uniforms/UniformVec4';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';

/**
 * Shader-side handles for the uniforms the shared contour-edge shader chunk
 * (`glsl/contour/contour-edge.glsl`) reads, named as the chunk declares
 * them. A shader that includes the chunk owns one of these, registers
 * {@link all} with its `storeUniformLocations`, and calls {@link load} once
 * per cross-section draw.
 */
export class ContourEdgeUniforms {
    public readonly viewToTexture = new UniformMatrix('viewToTextureUV');
    public readonly pixelSize = new UniformVec2('pixelSize');
    public readonly imageZRange = new UniformVec2('imageZRange');
    public readonly imageXYRange = new UniformVec4('imageXYRange');
    public readonly clipImageXY = new UniformBoolean('clipImageXY');

    /** Every handle, for the owning shader's `storeUniformLocations`. */
    public readonly all: readonly Uniform[] = [
        this.viewToTexture,
        this.pixelSize,
        this.imageZRange,
        this.imageXYRange,
        this.clipImageXY,
    ];

    /**
     * Load the chunk's uniforms for one cross-section draw of `texture` on
     * a plane view. The owning shader must be started.
     *
     * @param projectionViewMatrix The view's patient → NDC projection-view.
     * @param viewSizePx The view's size in pixels.
     * @param contourThickness Line thickness in pixels: the distance at
     *   which the shader samples a fragment's neighbours.
     * @param clipImageXY Whether the image's X/Y edges cut the structure.
     */
    public load(
        projectionViewMatrix: ReadonlyMat4,
        viewSizePx: ReadonlyVec2,
        texture: ContourTexture,
        contourThickness: number,
        clipImageXY: boolean
    ): void {
        // View NDC → patient for directions (the shader uses w = 0).
        const viewToPatient = mat4.invert(mat4.create(), projectionViewMatrix);

        // One pixel in NDC is 2/viewSize, scaled by the line thickness.
        this.pixelSize.loadVec2(
            vec2.fromValues(
                (2 * contourThickness) / viewSizePx[0],
                (2 * contourThickness) / viewSizePx[1]
            )
        );

        // Compose: view NDC direction → patient → texture UV [0,1]
        this.viewToTexture.loadMatrix(
            mat4.multiply(mat4.create(), texture.patientToImage, viewToPatient)
        );

        const imageUvRange = texture.getImageUvRange();
        this.imageZRange.loadVec2(
            vec2.fromValues(imageUvRange.min[2], imageUvRange.max[2])
        );
        this.imageXYRange.loadVec4(
            imageUvRange.min[0],
            imageUvRange.min[1],
            imageUvRange.max[0],
            imageUvRange.max[1]
        );
        this.clipImageXY.loadBoolean(clipImageXY);
    }
}
